// src/prototype/enterprise/scenarios/scenarioModel.ts
// ============================================================
// ENTERPRISE · SCENARIOS — client-side scenario math (Part A). NET-NEW compute over the Q1
// baseline, read from the spine via adapters only (getPortfolioAsOf / getRunCap /
// getCapitalAsks). No spine write.
//
// COMPOSITE MODEL (mirrors the spine's weighted-dimension scoring): a normalized weighted sum
// of the 4 dimensions. To keep the baseline consistent with the Portfolio tab (which shows the
// spine's AUTHORED Q1 composites), the live composite is anchored to the authored Q1 score and
// perturbed by how the current weights move the dimensions vs. an equal-weight baseline:
//
//   comp(w) = authored_Q1_composite + ( weightedSum(dims, w) − equalWeightAvg(dims) )
//
// At 25/25/25/25 this is exactly the authored Q1 composite (baseline == Portfolio Q1: 40 funded,
// hero uc 1555 = 82.0 @ #1). As weights shift, the weighted dimensions re-rank the portfolio live.
//
// FUNDING LINE: cumulative capital_ask walked down rank order until the cap is exhausted (NOT
// top-N) — the same mechanic the adapter uses for a stored run.
//
// This is PORTFOLIO-LEVEL scenario modeling (budget + weights across all initiatives) — distinct
// from the per-use-case ROI scenarios (conservative/base/aggressive) on an initiative's ROI page.
// ============================================================

import { getRuns, getRunCap, getCapitalAsks, getPortfolioAsOf } from '../../data/adapters/enterpriseSpine';

/** Raw slider values (not yet normalized). */
export interface ScenarioWeights {
  fi: number;
  ou: number;
  sa: number;
  if: number;
}

export const BASELINE_WEIGHTS: ScenarioWeights = { fi: 25, ou: 25, sa: 25, if: 25 };
/** The design's Q2 "Budget Reduction" profile — Financial Impact raised under margin pressure. */
export const Q2_WEIGHTS: ScenarioWeights = { fi: 35, ou: 25, sa: 20, if: 20 };
export const BASELINE_CAP = 50; // $M, resolved from the spine below; this is the slider default/marker

interface ScenBase {
  id: number;
  title: string;
  category: string;
  owner: { name: string; org: string };
  dims: [number, number, number, number];
  avg: number;
  base: number; // authored Q1 composite
  ask: number; // $M
  q1Rank: number;
}

export interface ScenarioBase {
  base: ScenBase[];
  baselineCap: number;
  q1FundedIds: Set<number>;
  q1FundedCount: number;
}

/** Load the Q1 baseline portfolio once (dims + authored composite + ask + Q1 rank/funded). */
export function loadScenarioBase(): ScenarioBase {
  const runs = getRuns();
  const q1Id = runs[0].run_id;
  const asof = getPortfolioAsOf(q1Id);
  const asks = getCapitalAsks();
  const base: ScenBase[] = (asof?.initiatives ?? []).map((i) => {
    const d = i.dimension_scores;
    const dims: [number, number, number, number] = [
      d.financial_impact, d.operational_urgency, d.strategic_alignment, d.implementation_feasibility,
    ];
    return {
      id: i.use_case_id,
      title: i.title,
      category: i.category,
      owner: i.owner,
      dims,
      avg: (dims[0] + dims[1] + dims[2] + dims[3]) / 4,
      base: i.composite,
      ask: asks[i.use_case_id] ?? 0,
      q1Rank: i.rank,
    };
  });
  const q1FundedIds = new Set((asof?.initiatives ?? []).filter((i) => i.funded).map((i) => i.use_case_id));
  return { base, baselineCap: getRunCap(q1Id), q1FundedIds, q1FundedCount: q1FundedIds.size };
}

export interface ScenRow {
  id: number;
  title: string;
  category: string;
  owner: { name: string; org: string };
  ask: number;
  comp: number;
  rank: number;
  q1Rank: number;
  move: number; // q1Rank − rank (positive ⇒ rose vs baseline)
  funded: boolean;
  crossing: boolean; // funded at Q1 baseline, not funded in this scenario
  atLine: boolean; // the last funded row (funding line sits just below it)
}

export interface ScenarioResult {
  rows: ScenRow[]; // full portfolio, ranked
  fundedCount: number;
  crossingCount: number;
  allocated: number; // $M committed below the line
}

/** Normalized weight percentages (sum to 100) for display. */
export function normalizedWeights(w: ScenarioWeights): { fi: number; ou: number; sa: number; if: number } {
  const tot = w.fi + w.ou + w.sa + w.if || 1;
  return {
    fi: Math.round((w.fi / tot) * 100),
    ou: Math.round((w.ou / tot) * 100),
    sa: Math.round((w.sa / tot) * 100),
    if: Math.round((w.if / tot) * 100),
  };
}

/** True when two lever sets are identical (used for the saved/modified badge). */
export function sameLevers(a: ScenarioWeights, aCap: number, b: ScenarioWeights, bCap: number): boolean {
  return aCap === bCap && a.fi === b.fi && a.ou === b.ou && a.sa === b.sa && a.if === b.if;
}

/** Re-score + re-rank the portfolio under the given weights, and place the funding line at cap. */
export function computeScenario(base: ScenarioBase, w: ScenarioWeights, cap: number): ScenarioResult {
  const tot = w.fi + w.ou + w.sa + w.if || 1;
  const scored = base.base
    .map((b) => {
      const ws = (b.dims[0] * w.fi + b.dims[1] * w.ou + b.dims[2] * w.sa + b.dims[3] * w.if) / tot;
      return { b, comp: b.base + (ws - b.avg) };
    })
    .sort((a, z) => z.comp - a.comp || a.b.id - z.b.id);

  let cum = 0;
  const fundedIds = new Set<number>();
  for (const s of scored) {
    if (cum + s.b.ask <= cap) {
      cum = Math.round((cum + s.b.ask) * 10) / 10;
      fundedIds.add(s.b.id);
    } else break; // funding line reached
  }
  const fundedCount = fundedIds.size;

  const rows: ScenRow[] = scored.map((s, i) => {
    const rank = i + 1;
    const funded = fundedIds.has(s.b.id);
    return {
      id: s.b.id,
      title: s.b.title,
      category: s.b.category,
      owner: s.b.owner,
      ask: s.b.ask,
      comp: s.comp,
      rank,
      q1Rank: s.b.q1Rank,
      move: s.b.q1Rank - rank,
      funded,
      crossing: base.q1FundedIds.has(s.b.id) && !funded,
      atLine: rank === fundedCount,
    };
  });

  return { rows, fundedCount, crossingCount: rows.filter((r) => r.crossing).length, allocated: cum };
}

// ── Compare-vs-baseline (the ROICommandCenter salvage shape) ──────────────────

export interface CompareSide {
  cap: number;
  weights: { fi: number; ou: number; sa: number; if: number }; // normalized %
  fundedCount: number;
  allocated: number;
}

export interface CompareRow {
  id: number;
  title: string;
  owner: { name: string; org: string };
  baseRank: number;
  baseComp: number;
  baseFunded: boolean;
  scenRank: number;
  scenComp: number;
  scenFunded: boolean;
  compDelta: number; // scenario − base
  rankDelta: number; // baseRank − scenRank (positive ⇒ rose)
  crossed: boolean; // funded at base, unfunded in scenario
}

export interface CompareResult {
  base: CompareSide;
  scenario: CompareSide;
  rows: CompareRow[]; // largest movers + funding-line crossings, by scenario rank
}

/**
 * Compare the current scenario against the Q1 baseline (25/25/25/25 @ baseline cap). Surfaces
 * the summary for each side plus the biggest movers and every funding-line crossing — the
 * base/Δ/scenario table shape salvaged from the per-use-case ROI command center.
 */
export function computeCompare(base: ScenarioBase, w: ScenarioWeights, cap: number): CompareResult {
  const baseline = computeScenario(base, BASELINE_WEIGHTS, base.baselineCap);
  const scenario = computeScenario(base, w, cap);
  const baseById = new Map(baseline.rows.map((r) => [r.id, r]));

  const movers = scenario.rows.map((sr) => {
    const br = baseById.get(sr.id)!;
    return {
      sr, br,
      compDelta: Math.round((sr.comp - br.comp) * 10) / 10,
      rankDelta: br.rank - sr.rank,
      crossed: br.funded && !sr.funded,
    };
  });
  const crossers = movers.filter((m) => m.crossed).slice(0, 3);
  const bigMovers = movers
    .filter((m) => !m.crossed && Math.abs(m.compDelta) > 0.01)
    .sort((a, b) => Math.abs(b.compDelta) - Math.abs(a.compDelta))
    .slice(0, 5);
  const rows: CompareRow[] = [...crossers, ...bigMovers]
    .sort((a, b) => a.sr.rank - b.sr.rank)
    .map((m) => ({
      id: m.sr.id,
      title: m.sr.title,
      owner: m.sr.owner,
      baseRank: m.br.rank,
      baseComp: m.br.comp,
      baseFunded: m.br.funded,
      scenRank: m.sr.rank,
      scenComp: m.sr.comp,
      scenFunded: m.sr.funded,
      compDelta: m.compDelta,
      rankDelta: m.rankDelta,
      crossed: m.crossed,
    }));

  const npct = normalizedWeights(w);
  return {
    base: { cap: base.baselineCap, weights: { fi: 25, ou: 25, sa: 25, if: 25 }, fundedCount: baseline.fundedCount, allocated: baseline.allocated },
    scenario: { cap, weights: npct, fundedCount: scenario.fundedCount, allocated: scenario.allocated },
    rows,
  };
}

// ── Saved scenario (client-side only; the spine is read-only) ─────────────────

export const SAVED_SCENARIO_KEY = 'dva_demo_enterprise_scenario';

export interface SavedScenario {
  name: string;
  cap: number;
  weights: ScenarioWeights;
}

export function loadSavedScenario(): SavedScenario | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SAVED_SCENARIO_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as SavedScenario;
    if (p && typeof p.cap === 'number' && p.weights) return p;
  } catch { /* ignore malformed */ }
  return null;
}

export function persistSavedScenario(s: SavedScenario): void {
  if (typeof window !== 'undefined') localStorage.setItem(SAVED_SCENARIO_KEY, JSON.stringify(s));
}
